import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { isPlatformBrowser, NgStyle } from '@angular/common';
import { fromEvent, Subscription } from 'rxjs';

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input,
  OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation
} from '@angular/core';

import {
  DataTableColumnTypeEnum, DeviceService, IComparator, IconSizeEnum, IProductClassification, IProductFeature, ITag,
  PositionEnum, SdAccordionComponent, SdAccordionGroupComponent, SdButtonComponent, SdButtonIconComponent,
  SdFormCheckboxComponent, SdTagComponent, SdThemeComponent, ThemesTextEnum, TypographyUtils
} from '@seb/web-design-system/core';

import { DataTableColumn, SdTableComponent } from '@seb/web-design-system/tables';
import { SdProductCardComponent } from '@seb/web-design-system/product-card';
import { GetValuePipe } from './getValue.pipe';

@Component({
  selector: 'sd-product-comparator',
  templateUrl: './product-comparator.component.html',
  styleUrls: ['./product-comparator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgStyle,
    SdFormCheckboxComponent,
    SdButtonIconComponent,
    SdProductCardComponent,
    SdTagComponent,
    SdButtonComponent,
    SdAccordionGroupComponent,
    SdAccordionComponent,
    SdTableComponent,
    TranslateModule
  ],
  providers: [
    GetValuePipe
  ]
})
export class SdProductComparatorComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input()
  promotedProduct: number;

  @Input()
  compareOnLoad = false;

  @HostBinding('class.is-readonly')
  @Input()
  readonly = false;

  @Input()
  isReadOnlyAccordion = false;

  @Input()
  classifications: IProductClassification[];

  @Input()
  entries: IComparator[];

  @Input()
  maxProducts = 4;

  @Input()
  maxLine = 4;

  @Input()
  isSSR = true;

  @Output()
  addProductToCart = new EventEmitter<string>();

  @Output()
  addNewProduct = new EventEmitter<string>();

  @Output()
  deleteProduct = new EventEmitter<string>();

  @Output()
  deleteLastProduct = new EventEmitter<void>();

  @Output()
  notifyProduct = new EventEmitter<string>();

  @Output()
  productClicked = new EventEmitter<string>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @ViewChild('stickyTableHeader')
  stickyTableHeader: ElementRef;

  @ViewChild('checkboxCompare')
  checkboxCompare: SdFormCheckboxComponent;

  @ViewChild('checkboxWrapper')
  checkboxWrapper: ElementRef;

  @ViewChild('comparatorTable')
  comparatorTable: ElementRef;

  @ViewChild('staticComparatorTable')
  staticComparatorTable: SdTableComponent;

  @ViewChild('staticComparatorTable', { read: ElementRef })
  staticComparatorTableElement: ElementRef;

  @ViewChild('accordionGroup')
  accordionGroup: SdAccordionGroupComponent;

  @ViewChild('accordionGroup', { read: ElementRef })
  accordionGroupElement: ElementRef;

  @Output()
  productSellerModal = new EventEmitter<boolean>();

  isMobile$: boolean;
  isDevice$: boolean;
  scrolled = false;
  tableHeadingHeight: number;
  stickyDifference: number;
  scrollMarginValue;
  simplifiedProductHeight = 185;
  iconSize = IconSizeEnum.small;
  positionEnum = PositionEnum;
  subscriptions = new Subscription();
  lastProduct = false;
  themesTextEnum = ThemesTextEnum;
  newColumns = [];
  newEntries = [];
  readonlyEntries = [];
  readonlyColumns = [];
  readonlyAccordionColumns = [];
  readonlyAccordionEntries = [];
  promotedProductTag: ITag;
  tableScrollLeft: number;
  isEmptyObject = TypographyUtils.isEmptyObject;
  canCompare = true;

  constructor(
    private cdRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private deviceService: DeviceService,
    private renderer: Renderer2
  ) {
    super();
  }

  @HostListener('window:scroll')
  onScroll() {
    this.scrollMarginValue = 0;
    const stickyTop = this.simplifiedProductHeight + this.scrollMarginValue;

    if (this.readonly && !this.isReadOnlyAccordion) {
      if (this.staticComparatorTableElement?.nativeElement.getBoundingClientRect().top < stickyTop) {
        this.simplifiedProductHeight = this.stickyTableHeader?.nativeElement.offsetHeight;
        this.scrolled = true;
      } else if (this.staticComparatorTableElement.nativeElement.getBoundingClientRect().top >= stickyTop) {
        this.scrolled = false;
        this.manageStickyHeading();
      }
    } else {
      if (this.accordionGroupElement?.nativeElement.getBoundingClientRect().top < stickyTop) {
        this.simplifiedProductHeight = this.stickyTableHeader?.nativeElement.offsetHeight;
        this.scrolled = true;
      } else if (this.accordionGroupElement?.nativeElement.getBoundingClientRect().top >= stickyTop) {
        this.scrolled = false;
        this.manageStickyHeading();
      }
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;

        if (!this.isMobile$ && isPlatformBrowser(this.platformId)) {
          this.tableScrollLeft = 0;
          this.accordionGroup?.accordions?.toArray().forEach((accordion) => {
            if (accordion.accordionChild) {
              this.renderer.setStyle(
                accordion.accordionChild?.table?.nativeElement,
                'transform',
                'translateX(' + this.tableScrollLeft + 'px',
              );
            }
          });
        }

        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isDevice.subscribe((x) => {
        this.isDevice$ = x;
        this.cdRef.markForCheck();
      })
    );

    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        fromEvent(window, 'resize').subscribe(() => {
          this.highlightPromotedProduct();
        })
      );

      this.generateTable();
      this.generateColumn();

      this.checkIfNeedComparison();

      if (this.checkboxCompare?.checked) {
        this.compare();
      }

      this.highlightPromotedProduct();
      this.generateReadonlyTable();
      this.lastProduct = this.entries.length === 1;
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.manageStickyHeading();
      }, 300);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    this.generateTable();
    this.generateColumn();

    this.generateReadonlyTable();
    this.checkIfNeedComparison();

    if (this.checkboxCompare?.checked) {
      this.compare();
    }

    if (
      ((changes['classifications'] &&
        !changes['classifications'].isFirstChange()) ||
        (changes['entries'] && !changes['entries'].isFirstChange())) &&
      this.checkboxCompare
    ) {
      if (this.entries.length === 1) {
        this.checkboxCompare.checked = false;
        this.lastProduct = true;
      } else {
        this.lastProduct = false;
      }
      this.toggleComparator(this.checkboxCompare.checked);
    }

    if (this.staticComparatorTable) {
      this.highlightPromotedProduct();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  manageStickyHeading() {
    this.tableHeadingHeight =
      this.stickyTableHeader?.nativeElement.getBoundingClientRect().height;

    this.stickyDifference =
      (this.tableHeadingHeight - this.simplifiedProductHeight) * -1 +
      this.scrollMarginValue;
  }

  highlightPromotedProduct(readMore?: boolean) {
    const promotedColumn = this.elRef.nativeElement.querySelectorAll('.c__selected-table');
    let checkBoxMargin = 0;
    let checkBoxSize = 0;

    if (
      this.checkboxWrapper?.nativeElement &&
      this.isMobile$ &&
      isPlatformBrowser(this.platformId)
    ) {
      checkBoxMargin = parseInt(window.getComputedStyle(this.checkboxWrapper?.nativeElement)?.marginBottom);
      checkBoxSize = this.checkboxWrapper?.nativeElement.getBoundingClientRect().height + checkBoxMargin;
    }

    if (
      this.staticComparatorTable &&
      !readMore &&
      this.staticComparatorTable?.showMoreButton &&
      isPlatformBrowser(this.platformId)
    ) {
      const margin = parseInt(
        window.getComputedStyle(this.staticComparatorTable?.showMoreButton?.nativeElement).marginTop,
      );

      const height = this.staticComparatorTable?.showMoreButton?.nativeElement.getBoundingClientRect().height;
      const totalHeight = margin + height + checkBoxSize;

      promotedColumn.forEach((prom) => {
        this.renderer.setStyle(
          prom,
          'height',
          'calc(100% - ' + totalHeight + 'px)',
        );
        this.renderer.setStyle(prom, 'top', checkBoxSize + 'px');
      });
    } else {
      promotedColumn.forEach((prom) => {
        this.renderer.setStyle(prom, 'height', '100%');
      });
    }

    if (this.promotedProduct > 0 && promotedColumn && this.promotedProduct <= this.entries.length) {
      this.subscriptions.add(
        this.translateService.get('product.tags.bestSeller').subscribe((text) => {
          this.setTranslate(text);
          this.cdRef.markForCheck();
        })
      );

      const operator = this.isMobile$ ? 1 : 0;
      const numberOfColumn = this.readonly
        ? this.entries.length > 3
          ? this.isDevice$
            ? 3.45
            : this.isMobile$
              ? 4
              : 5
          : this.isMobile$
            ? this.entries.length
            : this.entries.length + 1
        : this.entries.length < this.maxProducts
          ? this.entries.length +
            (this.maxProducts - this.entries.length) -
            operator
          : this.maxProducts + operator;
      const promotedColumnWidth = 100 / numberOfColumn + '%';
      const promotedColumnPosition =
        ((this.promotedProduct - operator) / numberOfColumn) * 100 + '%';
      promotedColumn.forEach((prom) => {
        this.renderer.setStyle(prom, 'width', promotedColumnWidth);
        this.renderer.setStyle(prom, 'left', promotedColumnPosition);
      });
    }
  }

  setTranslate(data: string) {
    this.promotedProductTag = {
      text: data
    };
  }

  generateReadonlyTable() {
    this.readonlyColumns = [];
    this.readonlyEntries = [];

    this.entries.forEach((product: IComparator, index: number) => {
      const newArr = [];
      this.newEntries.forEach((entry) => {
        newArr.push(entry[index]);
      });
      this.readonlyEntries.push(
        newArr.reduce((r, c) => Object.assign(r, c), {}),
      );
    });

    this.readonlyColumns = [].concat(...this.newColumns);
  }

  generateColumn() {
    this.newColumns = this.classifications?.map((category) => (
      category?.features.map((feature) => (
        new DataTableColumn(
          feature.code,
          { label: feature.name, media: feature.media },
          1,
          false,
          DataTableColumnTypeEnum.media,
          feature?.hidden
        )
      ))
    ));

    this.readonlyAccordionColumns = this.newColumns;
  }

  generateTable() {
    this.newEntries = [];

    this.classifications?.forEach((category: IProductClassification) => {
      const classificationsArr = [];
      this.entries?.forEach((product) => {
        const cellContentObject = {};
        product?.classifications
          .find((cat: IProductClassification) => cat.code === category.code)
          ?.features?.forEach((feature: IProductFeature) => {
            const code = feature.code;
            let value = feature.featureValues[0]?.value;
            const valueUnit = feature.featureUnit
              ? feature.featureUnit.symbol
              : undefined;
            if (valueUnit) {
              value = `${value} ${valueUnit}`;
            }
            cellContentObject[code] = { label: value };
          });
        classificationsArr.push(cellContentObject);
      });
      this.newEntries.push(classificationsArr);
    });

    this.readonlyAccordionEntries = this.newEntries;
  }

  onAdd() {
    this.addNewProduct.emit();
    this.updateFlexBasis();
  }

  onAddToCart(productCode: string) {
    this.addProductToCart.emit(productCode);
  }

  onDelete(index: number) {
    if (this.entries.length > 1) {
      this.deleteProduct.emit(this.entries[index].code);
    } else {
      this.deleteLastProduct.emit();
    }
    this.updateFlexBasis();
  }

  onNotify(event: string) {
    this.notifyProduct.emit(event);
  }

  toggleComparator(event: boolean) {
    if (event === true) {
      this.compare();
      return;
    }

    this.classifications.forEach((classification: IProductClassification) => {
      classification.features.forEach((feat: IProductFeature) => {
        feat.hidden = false;
      });
    });

    this.newColumns.forEach((column) => {
      column.forEach((line) => { line.hidden = false; });
      column.hidden = false;
    });
  }

  compare() {
    this.newColumns.forEach((classification, indexClass: number) => {
      const entryClass = this.newEntries[indexClass];
      classification.forEach((feature) => {
        feature.hidden = this.allAreEqual(
          entryClass.map((entryFeat) => entryFeat[feature.id]),
        );
      });
      classification.hidden = classification.every(
        (feature) => feature.hidden === true,
      );
    });
  }

  checkIfNeedComparison() {
    const classif = [];

    this.newColumns.forEach((classification, indexClass: number) => {
      const entryClass = this.newEntries[indexClass];
      classification.forEach((element) =>
        classif.push(
          this.allAreEqual(
            entryClass.map((entryFeat) => entryFeat[element.id]),
          ),
        ),
      );
    });

    this.canCompare = classif.some((classifs) => classifs === true);
  }

  allAreEqual(array) {
    if (!array.length) {
      return true;
    }

    if (array.every((el) => this.isEmptyObject(el))) {
      return true;
    }

    return (
      array.reduce((a, b) => {
        return a?.label?.toString().toLowerCase().trim() ===
          b?.label?.toString().toLowerCase().trim()
          ? a
          : !b;
      }) === array[0]
    );
  }

  updateFlexBasis() {
    const flexBasisValue =
      this.entries.length > this.maxProducts - 1
        ? 100 / (this.entries.length + 1) + '%'
        : '20%';

    this.subscriptions.add(
      this.accordionGroup?.accordions?.changes.subscribe((c) =>
        c.toArray().forEach((el) => {
          el.accordionChild?.table?.nativeElement
            .querySelectorAll('td, th')
            .forEach((td) => {
              this.renderer.setStyle(td, 'flex-basis', flexBasisValue);
            });
        }),
      )
    );

    this.accordionGroup?.accordions?.toArray().forEach((accordion) => {
      accordion.accordionChild?.table?.nativeElement
        .querySelectorAll('td, th')
        .forEach((td) => {
          this.renderer.setStyle(td, 'flex-basis', flexBasisValue);
        });
    });

    const allTableTd = this.comparatorTable.nativeElement.querySelectorAll('td');
    for (const td of allTableTd) {
      this.renderer.setStyle(td, 'flex-basis', flexBasisValue);
    }
  }

  onProductClick(event: string) {
    this.productClicked.emit(event);
  }

  toggleProductSellerModal(event) {
    this.productSellerModal.emit(event);
  }

  onTableScroll(event) {
    this.tableScrollLeft = event.target.scrollLeft * -1;

    if (this.isMobile$ && !this.readonly) {
      this.accordionGroup?.accordions?.toArray().forEach((accordion) => {
        if (accordion.accordionChild?.elRef?.nativeElement !== event.target) {
          this.renderer.setStyle(
            accordion.accordionChild?.table?.nativeElement,
            'transform',
            'translateX(' + this.tableScrollLeft + 'px',
          );
        }
      });
    }
  }

  onOpenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }
}
